import { createPortal } from "react-dom";

/**
 * Dismissable "What does this SSL warning mean?" info slide — reachable via a
 * small info button on the certificate-error card (`BarWebmail`'s `stage ===
 * "ssl"` branch). Same purely-explanatory contract as `BadUsbInfoModal.tsx`
 * (doesn't pause/advance the beat, just closes back to the warning). Reuses
 * the shared `.busb-info-*`/`.bue-*` glass-card typography for a consistent
 * voice, plus a small inline SVG diagram (no generated image needed) showing
 * the browser -> intercepting proxy -> real site chain, since that's the
 * whole point of the warning.
 */
function MitmDiagram() {
  return (
    <svg
      className="ssl-mitm-diagram"
      viewBox="0 0 360 120"
      role="img"
      aria-label="A proxy sitting between your browser and the real website, able to read everything in between"
    >
      <defs>
        <marker id="ssl-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill="#7fa8b8" />
        </marker>
      </defs>
      {/* browser */}
      <g transform="translate(6,30)">
        <rect width="72" height="52" rx="8" fill="#12222c" stroke="#2c4a58" strokeWidth="1.5" />
        <rect x="10" y="10" width="52" height="26" rx="3" fill="#0a161d" stroke="#254050" />
        <circle cx="36" cy="43" r="3.5" fill="#3f5a68" />
        <text x="36" y="66" textAnchor="middle" fontSize="10" fill="#9db6c2" fontWeight="700">
          You
        </text>
      </g>
      {/* connecting lines */}
      <line x1="80" y1="56" x2="140" y2="56" stroke="#7fa8b8" strokeWidth="2" markerEnd="url(#ssl-arrow)" />
      <line x1="222" y1="56" x2="282" y2="56" stroke="#7fa8b8" strokeWidth="2" markerEnd="url(#ssl-arrow)" />
      {/* the proxy, front and centre in warning red */}
      <g transform="translate(142,20)">
        <rect width="80" height="72" rx="10" fill="#2c1210" stroke="#d93025" strokeWidth="2" />
        <text x="40" y="30" textAnchor="middle" fontSize="20" fill="#ff6b5e">
          {"\u26A0"}
        </text>
        <text x="40" y="48" textAnchor="middle" fontSize="10" fill="#ffb3ab" fontWeight="800">
          UNKNOWN
        </text>
        <text x="40" y="60" textAnchor="middle" fontSize="10" fill="#ffb3ab" fontWeight="800">
          PROXY
        </text>
        {/* eye icon = it can read everything passing through */}
        <g transform="translate(28,60)" opacity="0.9">
          <path
            d="M0 4c3-5 9-8 12-8s9 3 12 8c-3 5-9 8-12 8s-9-3-12-8Z"
            fill="none"
            stroke="#ff8f85"
            strokeWidth="1.4"
          />
          <circle cx="12" cy="4" r="3" fill="#ff8f85" />
        </g>
      </g>
      {/* real site */}
      <g transform="translate(284,30)">
        <rect width="70" height="52" rx="8" fill="#0e2420" stroke="#2c584c" strokeWidth="1.5" />
        <rect x="10" y="10" width="50" height="8" rx="2" fill="#1f4a40" />
        <rect x="10" y="22" width="50" height="8" rx="2" fill="#1f4a40" />
        <rect x="10" y="34" width="30" height="8" rx="2" fill="#1f4a40" />
        <text x="35" y="66" textAnchor="middle" fontSize="10" fill="#8fc9b6" fontWeight="700">
          Real site
        </text>
      </g>
    </svg>
  );
}

export function SslInfoModal({ onClose }: { onClose: () => void }) {
  // Portalled to <body> so the card is NOT clipped/scaled by the 3D laptop
  // screen overlay it's triggered from (2026-07-29 "the info slide is a dead
  // end — continue is cut off"). Full-viewport wrap + a wide, flex-column card
  // whose body scrolls independently while the footer button stays pinned.
  const node = (
    <div className="busb-info-wrap ssl-info-wrap" role="dialog" aria-label="What does this SSL warning mean?">
      <div className="busb-info-card ssl-info-card">
        <button type="button" className="busb-info-close" onClick={onClose} aria-label="Close">
          {"\u2715"}
        </button>
        <div className="ssl-info-scroll">
        <div className="bue-kicker">SECURITY BASICS</div>
        <h2>What does this warning mean?</h2>
        <MitmDiagram />
        <p className="bue-lede">
          A browser shows this warning when it can&apos;t verify who it&apos;s really talking to.
          That usually means something is sitting BETWEEN you and the real site.
        </p>
        <div className="bue-block">
          <h3>It can be a man-in-the-middle proxy</h3>
          <p>
            On an untrusted network, traffic can be quietly routed through a proxy that presents
            its own fake certificate instead of the real site&apos;s. The browser notices the
            certificate doesn&apos;t check out and warns you &mdash; but nothing stops you from
            clicking through anyway.
          </p>
        </div>
        <div className="bue-block">
          <h3>Whoever runs that proxy can read everything</h3>
          <p>
            Once traffic flows through it, the proxy can see every page you load and everything
            you type or submit &mdash; including usernames and passwords &mdash; before quietly
            passing it on to the real site so nothing looks broken.
          </p>
        </div>
        <div className="bue-block">
          <h3>Why &quot;Continue anyway&quot; is the risky click</h3>
          <p>
            The warning is the browser&apos;s ONLY way of flagging this. Clicking past it trusts
            the connection anyway &mdash; there&apos;s no second check after that.
          </p>
        </div>
        <ul className="bue-facts">
          <li>A certificate warning is not a false alarm to click past &mdash; it&apos;s the browser refusing to vouch for the connection.</li>
          <li>On an unfamiliar Wi-Fi network, treat any certificate warning as a reason to stop, not to continue.</li>
          <li>When in doubt, close the tab and try again on a trusted network, or use mobile data instead.</li>
        </ul>
        </div>
        <div className="ssl-info-footer">
          <button type="button" className="btn busb-info-got-it" onClick={onClose}>
            Got it — close
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
